// CostEfficiencyDetail.jsx
// 15) Cost & efficiency — detailed guide
// Left TOC + scroll-spy + MAIN "Template + Example (Facebook-like Home Feed)" per section
// Right Sponsored (AdSense). Bootstrap UI. React Router v4/v5 compatible.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ====================================================================== */
/*  AdSense                                                               */
/* ====================================================================== */

const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
      return;
    }

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  Small layout helpers (readability-first)                               */
/* ====================================================================== */

function Section({ id, title, subtitle, children }) {
  return (
    <section id={id} className="mb-5">
      <div className="mb-2">
        <h2 className="h4 mb-1">{title}</h2>
        {subtitle ? <div className="text-muted">{subtitle}</div> : null}
      </div>

      {children}

      <div className="mt-3">
        <a href="#top" className="small text-muted">
          ↑ Back to top
        </a>
      </div>
    </section>
  );
}

function Sub({ title, right }) {
  return (
    <div className="d-flex align-items-start justify-content-between gap-2 mt-3 mb-2">
      <h3 className="h6 text-uppercase text-muted mb-0">{title}</h3>
      {right}
    </div>
  );
}

function Badge({ tone = "secondary", children, className = "" }) {
  return (
    <span className={`badge bg-${tone} ${className}`} style={{ whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function Muted({ children }) {
  return (
    <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
      {children}
    </div>
  );
}

function Card({ title, badge, children, className = "" }) {
  return (
    <div className={`card border-0 shadow-sm ${className}`}>
      <div className="card-body">
        {(title || badge) && (
          <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
            <div className="fw-semibold">{title}</div>
            {badge ? badge : null}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function Template({ title = "Template", children }) {
  return (
    <Card className="mt-3" title={title} badge={<Badge tone="secondary">Template</Badge>}>
      <Muted>{children}</Muted>
    </Card>
  );
}

function Example({ title = "Example (Facebook-like Home Feed)", children }) {
  return (
    <Card className="mt-3" title={title} badge={<Badge tone="success">Example</Badge>}>
      <Muted>{children}</Muted>
    </Card>
  );
}

function Takeaway({ children, tone = "primary" }) {
  return (
    <div className={`alert alert-${tone} py-2 mt-3 mb-0`} role="note">
      <div className="fw-semibold small mb-1">Takeaway</div>
      <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
        {children}
      </div>
    </div>
  );
}

function Pitfalls({ items }) {
  return (
    <div className="alert alert-warning small mt-3 mb-0">
      <div className="fw-semibold mb-1">Common pitfalls</div>
      <ul className="mb-0">
        {items.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

function Code({ title, children }) {
  return (
    <Card
      className="mt-3"
      title={title}
      badge={<Badge tone="secondary">Snippet</Badge>}
    >
      <pre
        className="bg-light rounded p-3 border small mb-0"
        style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
      >
        <code>{children}</code>
      </pre>
    </Card>
  );
}

function Checklist({ title, children }) {
  return (
    <Card className="mt-3" title={title} badge={<Badge tone="info">Checklist</Badge>}>
      <pre
        className="bg-light rounded p-3 border small mb-0"
        style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
      >
        <code>{children}</code>
      </pre>
    </Card>
  );
}

/* ====================================================================== */
/*  Example context (MAIN content, not left rail)                          */
/* ====================================================================== */

function ExampleContextCard({ system }) {
  return (
    <Card
      className="mb-4"
      title="Example context"
      badge={<Badge tone="primary">{system.name}</Badge>}
    >
      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <div className="fw-semibold small mb-1">Read path</div>
          <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
            {system.readPath}
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="fw-semibold small mb-1">Write path</div>
          <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
            {system.writePath}
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="fw-semibold small mb-1">Engagement</div>
          <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
            {system.engagementPath}
          </div>
        </div>
      </div>

      <hr className="my-3" />

      <div className="fw-semibold small mb-1">Cost hot spots to watch</div>
      <ul className="mb-0 text-muted small">
        {system.costHotspots.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </Card>
  );
}

/* ====================================================================== */
/*  TOC                                                                    */
/* ====================================================================== */

const TOC = [
  { id: "egress-storage-compression", label: "A) Egress, tiers, compression" },
  { id: "hot-warm-cold", label: "B) Hot/Warm/Cold + TTL & lifecycle" },
  { id: "caching-to-cut-cost", label: "C) Caching that reduces bills" },
  { id: "capacity-discounts", label: "D) Reserved vs Spot & commitments" },
  { id: "dollars-per-request", label: "E) $/request & $/tenant" },
  { id: "worked-example", label: "F) Worked example" },
  { id: "snippets", label: "Handy formulas & snippets" },
  { id: "bites", label: "Interview one-liners" },
];

/* ====================================================================== */
/*  Page                                                                   */
/* ====================================================================== */

export default function CostEfficiencyDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  const homeFeed = useMemo(
    () => ({
      name: "Facebook-like Home Feed",
      readPath:
        "GET /feed → fetch candidate postIds → hydrate posts/media → rank → rules → cursor",
      writePath:
        "POST /post → validate → store → async fanout/index/counters/notifications",
      engagementPath:
        "POST /like | /comment → idempotent write → counters → ranking features update",
      costHotspots: [
        "media egress (images/videos), origin vs CDN",
        "feed hydration fan-out (many reads per request)",
        "ranking compute (models/features), especially p99",
        "storage growth for events/logs/features (retention)",
      ],
    }),
    []
  );

  /* -------------------- scroll-spy -------------------- */
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActiveId(e.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Cost &amp; efficiency</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 rounded-3 p-3 p-md-4 text-center"
        style={{
          background:
            "linear-gradient(135deg, rgba(253,126,20,.06), rgba(32,201,151,.07))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-2">15) Cost &amp; efficiency — detailed guide</h1>
          <p className="text-muted mb-0">
            Practical ways to reduce cloud spend without hurting SLOs: egress awareness, storage tiers &amp;
            lifecycle, caching, commitments (reserved/spot), and tracking dollars per request and per tenant.
          </p>
        </div>
      </header>

      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <Card title="On this page">
              <nav className="nav flex-column small">
                {TOC.map(({ id, label }) => (
                  <a
                    key={id}
                    href={`#${id}`}
                    className={`nav-link px-0 ${
                      activeId === id ? "fw-semibold text-primary" : ""
                    }`}
                    aria-current={activeId === id ? "true" : undefined}
                  >
                    {label}
                  </a>
                ))}
              </nav>
            </Card>

            <div className="small text-muted mt-2 d-none d-xl-block">
              Tip: Use “Print” to save as PDF.
            </div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* Example context in MAIN (not left rail) */}
          <ExampleContextCard system={homeFeed} />

          {/* =============================================================== */}
          {/* A) Egress, storage tiers, compression                             */}
          {/* =============================================================== */}
          <Section
            id="egress-storage-compression"
            title="A) Egress awareness, storage tiers, and compression"
            subtitle="Most cloud bills are “bytes × distance × where.” Reduce bytes, reduce distance, and avoid expensive paths."
          >
            <Sub title="Egress: the silent killer" right={<Badge tone="light" className="text-dark border">Where cost explodes</Badge>} />
            <ul className="mb-0">
              <li>
                <strong>Know your egress paths:</strong> public internet (often expensive), cross-region, cross-AZ, and
                origin → CDN vs CDN → user.
              </li>
              <li>
                <strong>Push bytes to the edge:</strong> CDN caching + origin shielding so origin egress stays tiny.
              </li>
              <li>
                <strong>Prefer private connectivity:</strong> private endpoints/peering/backbone when it reduces public egress.
              </li>
            </ul>

            <Template title="Template — the egress playbook">
{`1) Inventory major byte flows:
   • Users ⇄ CDN ⇄ Origin
   • Service-to-service (cross-AZ? cross-region?)
   • Data pipelines exporting to analytics/partners

2) Make “origin egress” a KPI:
   • Track: origin GB/day, CDN hit ratio, cacheable % by route

3) Reduce bytes:
   • Text: Brotli/gzip
   • Media: correct formats + responsive sizes
   • APIs: avoid over-fetching; paginate; compress JSON

4) Reduce distance:
   • Keep compute + storage in the same region
   • Avoid cross-region reads and chatty calls across regions`}
            </Template>

            <Example title="Example — Home Feed egress & compression wins">
{`Home Feed byte flows:
• Biggest cost: images/videos in feed
• Goal: CDN hit ratio > 95% and avoid origin fetches

Tactics:
• Media served from CDN with versioned URLs (immutable cache)
• Signed URLs only when required; otherwise long max-age
• Brotli for JSON feed responses; keep payload lean (only fields needed for feed)
• Avoid “hydrating too much” (don’t fetch full post for every candidate)`}
            </Example>

            <Sub title="Storage tiers (object/block/db)" />
            <ul className="mb-0">
              <li>
                <strong>Object tiers:</strong> hot → warm (IA) → cold (archive). Cheaper as retrieval latency/fees go up.
              </li>
              <li>
                <strong>DB/storage:</strong> premium for hot OLTP; cheaper for warm replicas; snapshots/archives for cold retention.
              </li>
              <li>
                <strong>Lifecycle rules:</strong> transition by time + prefix/tags; auto-expire where allowed.
              </li>
            </ul>

            <Sub title="Compression & format hygiene" />
            <ul className="mb-0">
              <li>
                <strong>HTTP:</strong> Brotli for text (HTML/JS/CSS/JSON), gzip fallback; don’t recompress already-compressed media.
              </li>
              <li>
                <strong>Images:</strong> WebP/AVIF, responsive sizes, edge transforms.
              </li>
              <li>
                <strong>Analytics:</strong> Parquet/ORC + ZSTD/Snappy; avoid CSV/JSON for large datasets.
              </li>
            </ul>

            <Pitfalls
              items={[
                "Ignoring origin egress (you see CDN traffic but miss the expensive origin misses)",
                "No versioned URLs → low cacheability → unnecessary origin fetches",
                "Compressing images/video again (wastes CPU for little benefit)",
                "Over-fetching feed payloads and “hydrating everything” per request",
              ]}
            />

            <Takeaway>
              {`Most savings come from cutting bytes and origin egress: cache at CDN, keep payloads lean, and use the right media formats.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* B) Hot/Warm/Cold + TTL & archival                                 */}
          {/* =============================================================== */}
          <Section
            id="hot-warm-cold"
            title="B) Hot/Warm/Cold data, TTL, and archival"
            subtitle="Retention is a product decision. Automate it with lifecycle policies so storage doesn’t grow forever."
          >
            <Template title="Template — a retention & lifecycle plan">
{`1) Classify data:
   • user content, derived content (thumbnails), events, logs, traces, ML features, backups

2) For each class define:
   • business usefulness window (e.g., “feed relevance uses last 30 days”)
   • legal/compliance retention (if any)
   • access pattern (hot vs warm vs cold)

3) Encode lifecycle (automated):
   • hot (fast) → warm (cheaper) → cold (archive) → delete
   • include reporting: bytes by tier, object count, last access

4) Make queries cheaper:
   • partition by time
   • TTL indexes / table expiration
   • prune partitions and avoid scanning old data`}
            </Template>

            <Example title="Example — Home Feed data lifecycle">
{`Hot:
• feed cache heads, recent posts metadata, recent engagement counters (days-weeks)

Warm:
• older post metadata, analytics aggregates (months)

Cold:
• raw events/logs needed for audits (years, if required)
• old media variants not used (archive or delete)

Practical policy:
• logs: hot 14d → warm 90d → delete 365d
• events: hot 30d → warm 180d → archive 2y (if required)
• thumbnails: keep only active sizes; regenerate on demand if cheaper than storing all variants`}
            </Example>

            <Pitfalls
              items={[
                "No TTL → storage grows until the bill forces a fire drill",
                "Keeping all media variants forever (store explosion)",
                "OLTP DB used for OLAP analytics queries (expensive reads + performance risk)",
                "Retention rules exist in docs but not enforced by automation",
              ]}
            />

            <Takeaway tone="success">
              {`Automated TTL + lifecycle policies are the easiest long-term cost win. Don’t rely on humans to delete data.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* C) Caching that reduces bills                                     */}
          {/* =============================================================== */}
          <Section
            id="caching-to-cut-cost"
            title="C) Caching to cut cost (not just latency)"
            subtitle="Caching reduces DB reads, compute, and egress. But only if cacheability and keys are correct."
          >
            <Template title="Template — caching with a cost lens">
{`1) Decide what to cache:
   • expensive reads (hydration), feed heads, feature lookups, media
   • cache negative results briefly (404s) to stop repeated misses

2) Protect origin:
   • CDN + origin shielding
   • request coalescing (single-flight) for hot keys
   • jitter TTLs to avoid synchronized expirations

3) Track cache ROI:
   • Cache Hit Ratio (H)
   • Saved origin egress = total egress × (1 − H)
   • Saved compute/DB reads vs cache infra cost

4) Make cache safe:
   • versioned keys
   • explicit invalidation strategy (events) where needed
   • bounds: max size, eviction policy, per-tenant quotas`}
            </Template>

            <Example title="Example — Home Feed caching strategy">
{`CDN:
• Media is cache-first with long TTL + versioned URLs
• Origin shielding to prevent spikes from reaching origin

Redis:
• Cache feed “head” (first N postIds) per user for short TTL
• Cache hydration results (post metadata) with TTL and invalidation on PostUpdated

Anti-stampede:
• single-flight on user feed rebuild
• refresh-ahead for hot users
• jitter TTL to prevent thundering herd`}
            </Example>

            <Pitfalls
              items={[
                "Bad cache keys (missing query params or vary headers) → low hit ratio",
                "No stampede protection → cache makes meltdowns worse",
                "Caching huge objects without limits → eviction thrash",
                "Invalidation without a plan → stale content complaints and manual flushes",
              ]}
            />

            <Takeaway>
              {`Fix cacheability and keys before scaling infra. A higher hit ratio reduces egress + DB + CPU at once.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* D) Reserved vs Spot & commitments                                 */}
          {/* =============================================================== */}
          <Section
            id="capacity-discounts"
            title="D) Reserved vs Spot capacity and commitments"
            subtitle="Right-size first, then cover a stable baseline with commitments, and use on-demand/spot for burst."
          >
            <Template title="Template — commitment strategy that avoids regret">
{`1) Right-size before you commit:
   • measure CPU/mem/IO utilization and p95 latency
   • remove waste (oversized nodes, idle clusters, orphaned volumes)

2) Establish a baseline:
   • minimum steady capacity needed to meet SLOs

3) Cover baseline with discounts:
   • reserved instances / savings plans / committed use
   • keep flexibility (instance families/regions) if possible

4) Handle spikes:
   • autoscale on-demand
   • use spot/preemptible for interruptible workers (batch, async fanout, indexing)

5) Resilience for spot:
   • checkpointing, queue-based work, quick drain, retry with jitter`}
            </Template>

            <Example title="Example — Home Feed: what runs on spot vs reserved">
{`Reserved/baseline:
• core API fleet (GET /feed) baseline to meet SLOs
• primary DB and critical caches sized for peak-ish steady load

Spot/preemptible:
• async fanout workers
• media processing/transcoding
• re-indexing jobs (search / ranking feature backfills)
• offline analytics batch

Guardrails:
• queue depth alarms and max retry budgets
• graceful degradation if async capacity drops`}
            </Example>

            <Pitfalls
              items={[
                "Buying commitments before right-sizing (locks in waste)",
                "Putting stateful critical DB on spot without strong safeguards",
                "Autoscaling without stabilization → flapping and unpredictable bills",
                "No cost anomaly alerts (you learn in the invoice)",
              ]}
            />

            <Takeaway tone="success">
              {`Commitments are great for stable baselines. Put interruptible work on spot, but keep user-facing SLO paths on reliable capacity.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* E) $/request & $/tenant                                            */}
          {/* =============================================================== */}
          <Section
            id="dollars-per-request"
            title="E) Measuring $/request and $/tenant"
            subtitle="If you can’t measure unit economics, you can’t optimize cost safely."
          >
            <Template title="Template — cost allocation + unit economics">
{`1) Tag everything:
   • env, service, team, and (if multi-tenant) tenant_id / account_id

2) Define unit metrics:
   • requests, bytes_out, CPU_ms, DB_reads, cache_miss, queue_jobs

3) Compute:
   • $/request = total_service_cost / successful_requests
   • $/tenant = tenant_share_of_shared_cost + tenant_dedicated_cost
   • egress cost = GB_out × $/GB (separate CDN vs origin)

4) Publish dashboards:
   • $/request over time and by route
   • $/tenant/day top offenders
   • origin egress trend + CDN hit ratio
   • cost anomalies alerting`}
            </Template>

            <Example title="Example — Home Feed cost events you should capture">
{`Per request (GET /feed):
• tenant_id (or user segment)
• bytes_out
• hydration_calls_count
• cache_hit/miss
• ranker_cpu_ms
• db_rows_read
• served_media_from_cache? (yes/no)

Why:
• lets you see which routes/users drive cost
• catches regressions (e.g., new ranking model doubles CPU or bytes_out)`}
            </Example>

            <Code title="Snippet — rough $/tenant from request telemetry">
{`-- Example: approximate $/tenant for API egress + compute (monthly)
SELECT tenant_id,
       SUM(bytes_out) / 1e9 * 0.09      AS origin_egress_usd,  -- adjust pricing
       SUM(req_cpu_ms) / 1000 / 3600 
         * 0.12                         AS compute_usd,        -- e.g., $0.12 per vCPU-hour
       COUNT(*)                         AS requests,
       (SUM(bytes_out)/1e9*0.09 + SUM(req_cpu_ms)/1000/3600*0.12) / NULLIF(COUNT(*),0)
                                       AS usd_per_request
FROM cost_events
WHERE ts >= DATE_TRUNC('month', NOW())
GROUP BY tenant_id
ORDER BY origin_egress_usd DESC;`}
            </Code>

            <Pitfalls
              items={[
                "No tagging → everything is ‘shared’ and nobody owns the bill",
                "Only tracking totals (you need per-route and per-tenant breakdowns)",
                "Ignoring cost regressions in CI/CD (you find out after the invoice)",
                "Not separating CDN egress vs origin egress (wrong optimization focus)",
              ]}
            />

            <Takeaway>
              {`Treat cost as a product KPI: publish $/request and origin egress weekly so regressions are caught early.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* F) Worked example                                                  */}
          {/* =============================================================== */}
          <Section
            id="worked-example"
            title="F) Worked example (simple but useful)"
            subtitle="This is the kind of math interviewers love: show you can reason about cost drivers."
          >
            <Template title="Template — egress savings math">
{`Given:
• Total egress (GB/day)
• CDN hit ratio H
• Origin egress price ($/GB)
Compute:
• Origin GB/day = Total × (1 − H)
• Origin $/month ≈ Origin GB/day × $/GB × 30
Compare to “no CDN” cost to estimate savings.`}
            </Template>

            <Example title="Example — Home Feed: CDN hit ratio savings">
{`Scenario:
• Total egress to users: 1 TB/day (≈ 1024 GB/day)
• CDN hit ratio H = 95%
• Origin egress price: $0.09/GB

Origin egress:
• 1024 × (1 − 0.95) = 51.2 GB/day

Origin egress cost:
• 51.2 × 0.09 ≈ $4.61/day → ≈ $138/mo

No CDN origin cost:
• 1024 × 0.09 ≈ $92.16/day → ≈ $2,765/mo

Estimated monthly savings:
• ≈ $2,627/mo (before CDN costs)

Lesson:
• pushing media to CDN and keeping hit ratio high dominates savings.`}
            </Example>

            <Takeaway tone="success">
              {`Optimize the biggest multipliers first: bytes (media), hit ratio (CDN), and origin misses. Then tackle compute and storage.`}
            </Takeaway>
          </Section>

          {/* =============================================================== */}
          {/* Snippets                                                          */}
          {/* =============================================================== */}
          <Section id="snippets" title="Handy formulas & snippets" subtitle="A few quick tools you can reuse in interviews and design docs.">
            <Code title="Hit ratio & origin egress">
{`Origin_Egress_GB = Total_Egress_GB * (1 - Hit_Ratio)
Savings = (Total_Egress_GB - Origin_Egress_GB) * $/GB`}
            </Code>

            <Code title="Compute cost (instances)">
{`Compute$/mo = instance_count * $/hr * 730`}
            </Code>

            <Code title="Serverless cost (conceptual)">
{`$ = invocations * price_per_million / 1e6
  + GB-seconds * price_per_GB-second
  + egress`}
            </Code>

            <Checklist title="Lifecycle policy checklist">
{`• Hot → warm → cold → delete is automated
• Policy is tag/prefix-driven (no manual steps)
• Reports: bytes by tier, last access, object counts
• Restore drills for cold archives (prove you can retrieve)
• Partitions + TTL indexes reduce scan costs`}
            </Checklist>
          </Section>

          {/* =============================================================== */}
          {/* One-liners                                                        */}
          {/* =============================================================== */}
          <Section id="bites" title="Interview one-liners" subtitle="Short, confident lines to close your answer.">
            <ul className="mb-0">
              <li>“Egress dominates—push bytes to CDN, maximize hit ratio, and keep origin misses tiny.”</li>
              <li>“Automate retention with hot/warm/cold lifecycle policies; storage growth is a product decision.”</li>
              <li>“Caching is cost optimization: fix cache keys, prevent stampedes, and measure ROI.”</li>
              <li>“Right-size first; cover baseline with commitments; use spot for interruptible workers.”</li>
              <li>“We publish $/request and $/tenant—cost is a KPI, not a surprise invoice.”</li>
            </ul>

            {/* Optional in-article ad near bottom */}
            {ADS_ENABLED && (
              <div className="my-4 text-center d-print-none">
                <AdSlot
                  enabled={ADS_ENABLED}
                  adSlot="5201219465"
                  style={{ display: "block", minHeight: 90, textAlign: "center" }}
                />
              </div>
            )}
          </Section>
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
